import React, { useRef, useState } from 'react'
import { Box, GlobalStyles } from '@mui/material'
import { dsColorNew } from '@palette'
import { DateRangePickerSeeker } from '@ui-seeker/core'
import { format as formatDate } from 'date-fns'
import { pickerStateCache, rangeFromPicker, toIso, nextPending, pendingHint } from 'Pages/DsRepricing/@services/dateRange'

/**
 * One calendar for a `{from, to}` pair, everywhere DS Repricing asks
 * for a date range (Curro, 2026-10-02: "un calendario desplegable…
 * en todos lados con rango de fechas").
 *
 * Wraps the app's `DateRangePickerSeeker` (rsuite behind a MUI
 * FormControl — the same control Seasons already uses) so the three
 * screens that used to draw two `<input type="date">` boxes share one
 * look and one behaviour: ISO strings in and out, quick ranges on the
 * left of the calendar, 40px tall like every other small input.
 *
 * The wrapper's defaults suit a filter over history — it refuses any
 * day after today — which is the opposite of a price limit or a
 * schedule window. Every day is selectable here unless the caller says
 * otherwise.
 */
const FORMAT = 'dd MMM yyyy'
const ANY_DAY = () => false

// rsuite decides the toggle's height, not MUI (`.rs-*` classes), and
// its own emotion styles beat a plain sx block — hence `!important`.
const heightSx = {
	'& .rs-picker-toggle-wrapper, & .rs-picker-toggle, & .rs-picker-input, & .rs-input': {
		minHeight: '40px !important',
		height: '40px !important',
	},
	'& .rs-picker-toggle': {
		paddingTop: '6px !important',
		paddingBottom: '6px !important',
	},
	'& .rs-picker-toggle-value, & .rs-picker-toggle-placeholder': {
		fontSize: '14px !important',
	},
}

// The calendar opens in a portal, out of reach of the sx above. rsuite paints
// the OK button at 30% while the range is half picked — on white it reads as
// "there is no OK" (Curro, 2026-10-28). Still disabled, just visible; scoped
// to this field's menus through `menuClassName`.
const MENU_CLASS = 'ds-date-range-menu'
const menuStyles = {
	[`.${MENU_CLASS} .rs-picker-toolbar-right .rs-btn:disabled`]: { opacity: 0.55 },
}

export default function DateRangeField({
	from, to, onChange,
	label = '',
	placeholder = 'Any dates',
	cleanable = false,
	presets = [],
	shouldDisableDate = ANY_DAY,
	width = 260,
	sx = {},
	// Both off by default, so the three screens using this do not move
	// (CU-869fhtp3a, D2). `oneTap` is rsuite's: the first click confirms
	// and closes, so a range can only come from a preset — forwarded for
	// whoever wants that, not used by Activity. `collapseSameDay` paints
	// `[d, d]` as `31 Dec 2026` instead of `31 Dec 2026 → 31 Dec 2026`:
	// a day picked as click, click, OK reads as a day.
	oneTap = false,
	collapseSameDay = false,
}) {
	const fmt = (d) => formatDate(d, FORMAT)
	// Same `value` object while `from`/`to` do not change: a new one on every
	// render makes rsuite drop a half-picked range (see `pickerStateCache`).
	const stateOf = useRef(null)
	if (!stateOf.current) stateOf.current = pickerStateCache()
	const { value, placeholder: openEnd } = stateOf.current(from, to, fmt)
	const renderValue = collapseSameDay
		? ([a, b]) => (a && b && toIso(a) === toIso(b) ? fmt(a) : `${fmt(a)} → ${fmt(b)}`)
		: undefined

	// A range is click, click, OK; between the first click and the second
	// rsuite shows nothing but the painted square — the header keeps the
	// placeholder and the OK stays off. The first day is tracked here and
	// said in the placeholder (header and box) until the range is closed,
	// a preset applies, OK is pressed or the calendar closes.
	const [pending, setPending] = useState(null)
	const clearPending = () => setPending(null)
	const hint = pending ? pendingHint(pending, fmt) : null

	return (
		<Box sx={{ width, flex: 'none', bgcolor: dsColorNew.white, borderRadius: 1, ...heightSx, ...sx }}>
			<GlobalStyles styles={menuStyles}/>
			<DateRangePickerSeeker
				label={label}
				value={value}
				placeholder={hint || openEnd || placeholder}
				onChange={(v) => onChange(rangeFromPicker(v))}
				onSelect={(d) => setPending(p => nextPending(p, d))}
				onShortcutClick={clearPending}
				onOk={clearPending}
				onClose={clearPending}
				format={FORMAT}
				character=" ~ "
				cleanable={cleanable}
				ranges={presets}
				placement="bottomStart"
				menuClassName={MENU_CLASS}
				showWeekNumbers={false}
				shouldDisableDate={shouldDisableDate}
				oneTap={oneTap}
				renderValue={renderValue}
				block/>
		</Box>
	)
}
